import React, { useMemo, useState, useCallback } from "react";
import { Chip, makeStyles, Typography, Box, IconButton, Menu, MenuItem, Tooltip } from "@material-ui/core";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import SettingsIcon from "@material-ui/icons/Settings";
import { i18n } from "../../translate/i18n";
import OutlinedDiv from "../OutlinedDiv";
import useColumnAliases from "../../hooks/useColumnAliases";
import BroadcastTemplatePickerIcon from "../BroadcastTemplatePickerIcon";

const useStyles = makeStyles(theme => ({
    chip: {
        margin: theme.spacing(0.25),
        cursor: "pointer",
        height: "24px",
        fontSize: "0.7rem"
    },
    sectionTitle: {
        fontSize: "0.7rem",
        fontWeight: "bold",
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(0.25),
        textTransform: "uppercase"
    },
    sectionContainer: {
        marginBottom: theme.spacing(0.5)
    },
    chipsContainer: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(0.25)
    },
    iconRow: {
        display: "inline-flex",
        alignItems: "center",
        gap: theme.spacing(0.5),
    },
}));

function useVariableLists(getAlias) {
    return useMemo(() => {
        const contactVars = [
            { name: i18n.t("messageVariablesPicker.vars.contactName"), value: "{{name}} " },
            { name: "Email", value: "{{email}} " },
            { name: "Número", value: "{{numero}} " },
            { name: getAlias("Columna 4"), value: "{{colum4}} " },
            { name: getAlias("Columna 5"), value: "{{colum5}} " },
            { name: getAlias("Columna 6"), value: "{{colum6}} " },
            { name: getAlias("Columna 7"), value: "{{colum7}} " },
            { name: getAlias("Columna 8"), value: "{{colum8}} " },
            { name: getAlias("Columna 9"), value: "{{colum9}} " },
            { name: getAlias("Columna 10"), value: "{{colum10}} " },
        ];
        const systemVars = [
            { name: i18n.t("messageVariablesPicker.vars.contactFirstName"), value: "{{firstName}}" },
            { name: i18n.t("messageVariablesPicker.vars.greeting"), value: "{{ms}} " },
            { name: i18n.t("messageVariablesPicker.vars.protocolNumber"), value: "{{protocol}} " },
            { name: i18n.t("messageVariablesPicker.vars.hour"), value: "{{hora}} " },
        ];
        return { contactVars, systemVars };
    }, [getAlias]);
}

const MessageVariablesPicker = ({ onClick, disabled, variant = "chips", onTemplateSelect }) => {
    const classes = useStyles();
    const { getAlias } = useColumnAliases();
    const { contactVars, systemVars } = useVariableLists(getAlias);
    const [contactAnchor, setContactAnchor] = useState(null);
    const [systemAnchor, setSystemAnchor] = useState(null);

    const handleChipClick = (e, value) => {
        e.preventDefault();
        if (disabled) return;
        onClick(value);
    };

    const pickFromMenu = useCallback((value) => {
        if (disabled) return;
        onClick(value);
    }, [disabled, onClick]);

    const renderIconMenus = () => (
        <Box className={classes.iconRow}>
            {onTemplateSelect && (
                <BroadcastTemplatePickerIcon
                    disabled={disabled}
                    onSelect={onTemplateSelect}
                />
            )}
            <Tooltip title={i18n.t("messageVariablesPicker.tooltipContactVars")}>
                <span>
                    <IconButton
                        size="small"
                        aria-label={i18n.t("messageVariablesPicker.tooltipContactVars")}
                        disabled={disabled}
                        onClick={e => setContactAnchor(e.currentTarget)}
                    >
                        <MenuBookIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Menu
                anchorEl={contactAnchor}
                open={Boolean(contactAnchor)}
                onClose={() => setContactAnchor(null)}
                PaperProps={{ style: { maxHeight: 360 } }}
            >
                {contactVars.map(msgVar => (
                    <MenuItem
                        key={msgVar.value}
                        dense
                        onClick={() => {
                            pickFromMenu(msgVar.value);
                            setContactAnchor(null);
                        }}
                    >
                        <Typography variant="body2">{msgVar.name}</Typography>
                    </MenuItem>
                ))}
            </Menu>
            <Tooltip title={i18n.t("messageVariablesPicker.tooltipSystemVars")}>
                <span>
                    <IconButton
                        size="small"
                        aria-label={i18n.t("messageVariablesPicker.tooltipSystemVars")}
                        disabled={disabled}
                        onClick={e => setSystemAnchor(e.currentTarget)}
                    >
                        <SettingsIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Menu
                anchorEl={systemAnchor}
                open={Boolean(systemAnchor)}
                onClose={() => setSystemAnchor(null)}
                PaperProps={{ style: { maxHeight: 360 } }}
            >
                {systemVars.map(msgVar => (
                    <MenuItem
                        key={msgVar.value}
                        dense
                        onClick={() => {
                            pickFromMenu(msgVar.value);
                            setSystemAnchor(null);
                        }}
                    >
                        <Typography variant="body2">{msgVar.name}</Typography>
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );

    if (variant === "iconMenus") {
        return renderIconMenus();
    }

    return (
        <OutlinedDiv
            margin="dense"
            fullWidth
            label={i18n.t("messageVariablesPicker.label")}
            disabled={disabled}
        >
            <Box className={classes.sectionContainer}>
                <Typography className={classes.sectionTitle}>
                    <span role="img" aria-label="Lista de contactos">📋</span> Variables del Contacto
                </Typography>
                <Box className={classes.chipsContainer}>
                    {contactVars.map(msgVar => (
                        <Chip
                            key={msgVar.value}
                            onMouseDown={e => handleChipClick(e, msgVar.value)}
                            label={msgVar.name}
                            size="small"
                            className={classes.chip}
                            color="primary"
                        />
                    ))}
                </Box>
            </Box>
            <Box className={classes.sectionContainer}>
                <Typography className={classes.sectionTitle}>
                    <span role="img" aria-label="Configuración del sistema">⚙️</span> Variables del Sistema
                </Typography>
                <Box className={classes.chipsContainer}>
                    {systemVars.map(msgVar => (
                        <Chip
                            key={msgVar.value}
                            onMouseDown={e => handleChipClick(e, msgVar.value)}
                            label={msgVar.name}
                            size="small"
                            className={classes.chip}
                            color="secondary"
                        />
                    ))}
                </Box>
            </Box>
        </OutlinedDiv>
    );
};

export default MessageVariablesPicker;
